import { ArrowButton, TextButton } from 'components/Buttons'
import {
  Caption,
  HeadlineM,
  HeadlineS,
  Overline,
  Subtitle,
  Subtitle2,
} from 'components/Text'
import { Link } from 'gatsby'
import React, { Dispatch, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import Colors from 'styles/Colors'
import { ContentfulArticle } from 'Types'
import { formatPath, getBulletColor, getFilters } from './utils'
import placeholder from 'src/assets/images/resources.jpg'
import media from 'styles/media'
import { ReactComponent as ArrowSVG } from 'svg/arrowSmall.svg'
import { ReactComponent as SearchSVG } from 'svg/search.svg'
import { ReactComponent as CloseSVG } from 'svg/close.svg'

type Props = {
  articles: ContentfulArticle[]
  allArticles: ContentfulArticle[]
  type: string
  setType: Dispatch<any>
  category: string
  setCategory: Dispatch<any>
  supportSelected: boolean
}

const ArticleList: React.FC<Props> = ({
  articles,
  allArticles,
  type,
  setType,
  category,
  setCategory,
}) => {
  const [title, setTitle] = useState('Recent topics')
  const [articleAmount, setArticleAmount] = useState(6)
  const [canShowMore, setCanShowMore] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (type === 'Blog') {
      setTitle('Recent articles')
    }
    if (type === 'News') {
      setTitle('Recent news')
    }
    if (type === 'Guides') {
      setTitle('Recent guides')
    }
    if (type === 'Support') {
      setTitle('Troubleshooting')
    }
    setArticleAmount(6)
  }, [type])

  useEffect(() => {
    if (articleAmount >= articles.length) {
      setCanShowMore(false)
    }
  }, [type, articles, articleAmount])

  useEffect(() => {
    setArticleAmount(6)
    setCanShowMore(true)
  }, [type])

  const loadMore = useCallback(() => {
    if (articleAmount + 3 >= articles.length) {
      setArticleAmount(articles.length)
    } else {
      setArticleAmount(articleAmount + 3)
    }
  }, [articles, articleAmount, type])

  const handleClose = (e) => {
    e.stopPropagation()

    setType('')
  }

  function renderArticles(articles) {
    const articlesToRender = [...articles].slice(0, articleAmount)
    return articlesToRender.map((article) => {
      return (
        <Link
          to={`/${formatPath(article.title)}`}
          key={article.title}
          state={{ lastType: type, lastCategory: category }}
        >
          <Article>
            <ArticleImage
              url={
                article.coverImage
                  ? article.coverImage.file
                    ? article.coverImage.file.url
                    : placeholder
                  : placeholder
              }
            />
            <ArticleLabel bulletColor={getBulletColor(article.type)} bullet>
              {article.category.split('-')[0]}
            </ArticleLabel>
            <ArticleTitle>{article.title}</ArticleTitle>
            <ArticleTopic>{article.type}</ArticleTopic>
            <Read>
              Read
              <Arrow />
            </Read>
          </Article>
        </Link>
      )
    })
  }

  function renderFilters() {
    const filters = getFilters(allArticles, type)
    const color = getBulletColor(type)
    return (
      <>
        {filters ? (
          <FiltersTitle>Categories</FiltersTitle>
        ) : (
          <FiltersTitle>Topics</FiltersTitle>
        )}
        <Topics>
          <TopicButton
            active={type === 'Blog'}
            onClick={() => {
              setType('Blog')
              setCategory('')
            }}
          >
            <Topic>
              Blog
              {type === 'Blog' && <Close onClick={handleClose} />}
            </Topic>
          </TopicButton>
          <TopicButton
            active={type === 'News'}
            onClick={() => {
              setType('News')
              setCategory('')
            }}
          >
            <Topic>
              News
              {type === 'News' && <Close onClick={handleClose} />}
            </Topic>
          </TopicButton>
          <Link to="/support">
            <TopicButton active={false}>
              <Topic>Support</Topic>
            </TopicButton>
          </Link>
        </Topics>
        {filters && (
          <FilterButton onClick={() => setCategory('')}>
            <ButtonContent bulletColor={color} active={category === ''}>
              {type === 'Blog'
                ? 'Recent articles'
                : type === 'News'
                ? 'Recent news'
                : type === 'Guides'
                ? 'Popular guides'
                : 'Troubleshooting'}
            </ButtonContent>
          </FilterButton>
        )}
        {filters &&
          filters.map((filter, i) => (
            <FilterButton
              key={i}
              onClick={() => {
                setCategory(filter)
                setTitle(filter)
              }}
            >
              <ButtonContent bulletColor={color} active={category === filter}>
                {filter}
              </ButtonContent>
            </FilterButton>
          ))}
      </>
    )
  }

  const filterArticles = (articles) => {
    let newArticles = [...articles]

    if (search) {
      newArticles = articles.filter((item) => {
        if (item.category.toLowerCase().includes(search.toLowerCase())) {
          return item
        }
        if (item.title.toLowerCase().includes(search.toLowerCase())) {
          return item
        }
        if (item.type.toLowerCase().includes(search.toLowerCase())) {
          return item
        }
        if (item?.author?.name.toLowerCase().includes(search.toLowerCase())) {
          return item
        }
        if (item?.content?.raw.toLowerCase().includes(search.toLowerCase())) {
          return item
        }
        let tags = item?.tags?.map((item) => item.toLowerCase())
        if (tags?.includes(search.toLowerCase())) {
          return item
        }
      })
    }

    return newArticles
  }

  let filteredArticles = filterArticles(articles)
  let renderedArticles = renderArticles(filteredArticles)

  return (
    <Wrapper>
      <FlexContainer>
        <MainTitle>Resources</MainTitle>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a topic"
        />
        <SearchIcon />
      </FlexContainer>
      <Row>
        <Filters>{renderFilters()}</Filters>
        <Articles>
          <Title>{title}</Title>
          {renderedArticles}
          {canShowMore && filteredArticles.length > articleAmount && (
            <More onClick={loadMore}>Load more articles</More>
          )}
          {!renderedArticles.length && <P>We couldn't find any articles.</P>}
        </Articles>
      </Row>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  position: relative;
  padding-left: 96px;
  display: flex;
  flex-direction: column;
  padding-top: 208px;
  padding-right: 150px;
  @media screen and (max-width: 1550px) {
    display: block;
  }
  ${media.tabletLandscape} {
    padding-left: 48px;
    padding-right: 48px;
  }
  ${media.mobile} {
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 100px;
  }
`

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 116px;
  position: relative;

  ${media.fullWidth} {
    width: calc(888px + 28.6vw + 1.4vw);
  }

  ${media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0px;
  }
`

const Row = styled.div`
  display: flex;
  flex-direction: column;

  ${media.fullWidth} {
    flex-direction: row;
  }
`

const MainTitle = styled(HeadlineM)`
  ${media.tablet} {
    font-size: 36px;
  }
  ${media.mobile} {
    margin-bottom: 48px;
  }
  ${media.fullWidth} {
    font-size: 50px;
  }
`
const Title = styled(HeadlineS)`
  position: absolute;
  top: -48px;
  left: 0;
  font-weight: 500;
  font-size: 36px;
  @media screen and (max-width: 1550px) {
    top: 0;
  }
  ${media.mobile} {
    font-size: 36px;
    line-height: 1em;
  }
  @media screen and (max-width: 300px) {
    font-size: 24px;
  }
`
const Filters = styled.div`
  width: 28.6vw;
  border-top: solid 1px ${Colors.softBlack};
  margin-top: 1.9vw;
  margin-right: 1.4vw;
  ${media.mobile} {
    width: 100%;
    margin: 24px 0 43px;
  }
`
const FiltersTitle = styled(Subtitle2)`
  margin-top: 12px;
  margin-bottom: 24px;
`
const FilterButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  ${media.mobile} {
    margin-bottom: 20px;
  }
`
const ButtonContent = styled.p<{ active: boolean; bulletColor: string }>`
  font-size: 16px;
  color: ${Colors.softBlack};
  opacity: ${(props) => (props.active ? 1 : 0.48)};
  display: flex;
  align-items: center;
  transition: 800ms cubic-bezier(0.72, 0, 0.12, 1);
  ::before {
    content: ' ';
    width: ${(props) => (props.active ? '15px' : '0px')};
    margin-right: ${(props) => (props.active ? '9px' : '0px')};
    height: 2px;
    background: ${(props) => props.bulletColor};
    transition: 800ms cubic-bezier(0.72, 0, 0.12, 1);
  }
`

const P = styled.p`
  font-size: 16px;
  color: ${Colors.softBlack};
  display: flex;
  margin-top: 60px;
`
const Topics = styled.div`
  display: flex;
  margin-bottom: 48px;
  a {
    display: flex;
    align-items: center;
  }
`
const TopicButton = styled.button<{ active: boolean }>`
  padding: 0;
  opacity: ${(props) => (props.active ? 1 : 0.25)};
  border: none;
  background: transparent;
`
const Topic = styled(Caption)`
  padding: 4px 12px;
  background: ${Colors.softBlack};
  color: white;
  margin-right: 8px;
  display: flex;
  align-items: center;
`
const Articles = styled.div`
  width: 100%;
  margin-top: 1.9vw;
  padding-bottom: 48px;
  position: relative;
  @media screen and (max-width: 1550px) {
    padding-top: 64px;
  }
  ${media.tabletLandscape} {
  }
  ${media.tablet} {
  }
  ${media.mobile} {
    padding-bottom: 64px;
  }

  ${media.fullWidth} {
    width: 888px;
  }
`
const Article = styled.div`
  min-height: 204px;
  background: white;
  margin-bottom: 24px;
  position: relative;
  padding: 24px 206px 44px 224px;
  width: 100%;

  ${media.mobile} {
    min-height: 355px;
    max-width: 351px;
    padding: 0;
    padding-bottom: 41px;
    margin: 0 auto 24px;
  }
`
const ArticleLabel = styled(Overline)<{ bulletColor: string }>`
  margin-bottom: 12px;
  ::after {
    color: ${(props) => props.bulletColor};
  }
  ${media.mobile} {
    margin: 12px;
  }
`
const ArticleTitle = styled(Subtitle)`
  margin-bottom: 24px;
  max-width: 458px;
  ${media.mobile} {
    margin-left: 12px;
    margin-right: 12px;
    margin-bottom: 21px;
  }
`
const ArticleTopic = styled(Topic)`
  position: absolute;
  bottom: 24px;
  ${media.mobile} {
    bottom: 21px;
    left: 12px;
  }
`
const ArticleImage = styled.div<{ url: string }>`
  width: 196px;
  height: calc(100% - 8px);
  position: absolute;
  background: url(${(props) => props.url}) center no-repeat;
  background-size: cover;
  left: 4px;
  top: 4px;
  ${media.mobile} {
    position: static;
    width: 100%;
    height: 140px;
  }
`
const Read = styled.button`
  position: absolute;
  top: 94px;
  right: 48px;
  transition: 0.4s;
  transition-timing-function: cubic-bezier(0.72, 0, 0.12, 1);
  border: none;
  background: transparent;
  displau: flex;
  cursor: pointer;
  color: ${Colors.softBlack};
  font-size: 16px;
  font-weight: 500;

  :hover {
    right: 44px;
  }
  ${media.mobile} {
    bottom: 21px;
    top: auto;
    right: 12px;
    :hover {
      right: 8px;
      color: ${Colors.softBlack48};
      svg {
        margin-left: 12px;
        color: ${Colors.softBlack};
      }
    }
  }
`
const Arrow = styled(ArrowSVG)`
  width: 14px;
  margin-left: 8px;
  transition: 0.4s;
  transition-timing-function: cubic-bezier(0.72, 0, 0.12, 1);
`
const More = styled(TextButton)`
  width 181px;
  position: absolute;
  margin: 0;
  left: calc(50% - 90px);
  font-size: 16px;
  ${media.mobile} {
    display: block;
  }
`

const Input = styled.input`
  background: rgba(124, 161, 161, 0.12);
  height: 40px;
  border: none;
  outline: none;
  color: ${Colors.softBlack};
  padding-left: 60px;
  width: 350px;
  font-family: Maison Neue;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;

  ::placeholder {
    color: ${Colors.softBlack};
  }

  ${media.mobile} {
    border: 1px solid ${Colors.softBlack};
    height: 48px;
    width: 100%;
  }
`

const SearchIcon = styled(SearchSVG)`
  position: absolute;
  height: auto;
  width: 24px;
  top: 16px;
  right: 305px;

  ${media.tablet} {
    top: 8px;
  }

  ${media.mobile} {
    right: unset;
    left: 20px;
    top: 97px;
  }

  ${media.fullWidth} {
    top: 14px;
  }
`

const Close = styled(CloseSVG)`
  height: auto;
  width: 0.6vw;
  margin-left: 0.5vw;

  ${media.tabletLandscape} {
    width: 0.8vw;
    margin-left: 0.8vw;
  }

  ${media.tablet} {
    width: 1.1vw;
    margin-left: 1.3vw;
  }

  ${media.mobile} {
    width: 1.8vw;
    margin-left: 2vw;
  }

  ${media.fullWidth} {
    width: 8px;
    margin-left: 10px;
  }
`

export default ArticleList
